import { useEffect, useState } from 'react';
import { XMasonry, XBlock } from 'react-xmasonry';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { Card } from 'components/Cards/card/card';
import { GetLearningCenter, GetLiveTraining } from 'api/training';
import LoadingIndicator from 'components/Global/loading-indicator/loading-indicator';
import { StrikeThroughHeader } from 'components/Headers/strikethrough-header/strikethrough-header';
import '../Index/Index.scss';
import { useQuery } from '@tanstack/react-query';
import CategoryAccordion from '../../../components/Cards/card/CategoryAccordion';

export default function LearningCenter({ categorizeArray }) {
  const [parsedLcData, setParsedLcData] = useState([]);
  const [parsedLtData, setParsedLtData] = useState([]);

  const {
    data: lcData,
    isPending: lcLoading,
    isError: lcError,
    isSuccess: lcSuccess,
    refetch: refetchLc,
  } = useQuery({
    queryKey: ['getLearningCenter'],
    queryFn: () => GetLearningCenter()(),
  });

  const {
    data: ltData,
    isPending: ltLoading,
    isError: ltError,
    isSuccess: ltSuccess,
    refetch: refetchLt,
  } = useQuery({
    queryKey: ['getLiveTraining'],
    queryFn: () => GetLiveTraining()(),
  });

  useEffect(() => {
    if (lcSuccess && lcData) {
      setParsedLcData(categorizeArray(JSON.parse(lcData)));
    }
  }, [lcSuccess, lcData]);

  useEffect(() => {
    if (ltSuccess && ltData) {
      setParsedLtData(JSON.parse(ltData));
    }
  }, [ltSuccess, ltData]);

  return (
    <>
      <StrikeThroughHeader>Learning Center</StrikeThroughHeader>
      <div className="page-inner-content">
        {(lcLoading || ltLoading) && <LoadingIndicator />}
        {(lcError || ltError) && (
          <RetryView
            message="Failed to load Learning Center Items"
            callback={() => {
              refetchLc();
              refetchLt();
            }}
          />
        )}
        {parsedLcData && parsedLtData && (
          <XMasonry maxColumns={3} responsive targetBlockWidth={500}>
            {parsedLcData.map((category, index) => (
              <XBlock key={category.name}>
                <CategoryAccordion
                  key={category.name}
                  category={category}
                  index={index}
                  baseUrl={`${process.env.REACT_APP_AGENTPORTAL_PREFIX}/LearningCenter`}
                />
              </XBlock>
            ))}
            {parsedLtData.length > 0 && (
              <XBlock width={3}>
                <div className="m-2 mt-5">
                  <Card
                    title="Live Training"
                    height="auto"
                    headingBgColor="#add8e6"
                    headingFontColor="white"
                    headingHeight="3em"
                    width="100%"
                  >
                    <div id="live-training">
                      <Alert severity="info">
                        Click or hover over a Live Training Item for options.
                      </Alert>
                      {parsedLtData.map((ltItem) => (
                        <LiveTrainingItem
                          Title={ltItem.Title}
                          Start={new Date(ltItem.StartDateTime)}
                          End={new Date(ltItem.EndDateTime)}
                          Summary={ltItem.Summary}
                          ID={ltItem.ID}
                        />
                      ))}
                    </div>
                  </Card>
                </div>
              </XBlock>
            )}
          </XMasonry>
        )}
      </div>
    </>
  );
}

function LiveTrainingItem({ Title, Start, End, Summary, ID }) {
  const [fileURL, setFileURL] = useState();
  const [fileLoading, setFileLoading] = useState(false);
  const [dtCopied, setdtCopied] = useState(false);
  const [infoCopied, setInfoCopied] = useState(false);
  const [options, setOptions] = useState(false);

  useEffect(() => {
    if (fileURL) {
      window.open(fileURL);
    }
  }, [fileURL]);

  useEffect(() => {
    if (dtCopied) {
      setTimeout(() => {
        setdtCopied(false);
      }, 1000);
    }
  }, [dtCopied]);

  useEffect(() => {
    if (infoCopied) {
      setTimeout(() => {
        setInfoCopied(false);
      }, 1000);
    }
  }, [infoCopied]);

  const getFile = () => {
    setFileLoading(true);
    DownloadICS(ID)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network Response: ', response);
        }
        setFileLoading(false);
        setFileURL(response.url);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const ValueCopied = () => (
    <div className="value-copied hover-overlay-text">Copied to clipboard!</div>
  );

  return (
    <div
      key={Title}
      className="live-training-item"
      onBlur={() => setOptions(false)}
      onMouseLeave={() => setOptions(false)}
    >
      <div className="title" onClick={() => setOptions(!options)}>
        {Title}
      </div>
      <div
        className={`info${options ? ' ' + 'options-active' : ''}`}
        onClick={() => (!options ? setOptions(false) : '')}
      >
        <div className="time">
          <div className="time-title">Date and Time</div>
          <div className="inner-info">
            {`${Start.toLocaleDateString()} ${Start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
            - {End.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
        <div className="summary">
          <div className="summary-title">Information</div>
          <div className="inner-info">{Summary}</div>
        </div>
        <div className="hover-overlay">
          <div className="download-invite hover-overlay-item">
            <div
              className="hover-overlay-text"
              onClick={() => {
                if (!fileURL && !fileLoading) {
                  getFile();
                } else if (!fileLoading) {
                  window.open(fileURL);
                }
              }}
            >
              {!fileLoading ? <>Download Invite</> : <CircularProgress />}
            </div>
          </div>
          <div className="date-time-copy hover-overlay-item">
            {!dtCopied ? (
              <CopyToClipboard
                text={`${Start.toLocaleDateString()} ${Start.toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}-${End.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
                onCopy={() => setdtCopied(true)}
              >
                <div className="hover-overlay-text">Copy Date and Time</div>
              </CopyToClipboard>
            ) : (
              <ValueCopied />
            )}
          </div>
          <div className="info-copy hover-overlay-item">
            {!infoCopied ? (
              <CopyToClipboard text={Summary} onCopy={() => setInfoCopied(true)}>
                <div className="hover-overlay-text">Copy Information</div>
              </CopyToClipboard>
            ) : (
              <ValueCopied />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
